import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapPin, Ticket, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "nf_proximity_optin";

// Portale cliente: chiede (una volta) il permesso posizione e, se il cliente è entro il raggio di una sede, mostra il banner con testo sede + coupon attivo
export default function ProximityWatcher({ card, sessionToken, brand }) {
  const [optin, setOptin] = useState(() => localStorage.getItem(KEY));
  const [hit, setHit] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const timer = useRef(null);
  const check = async () => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await axios.post(`${API}/public/customer/${card}/nearby`, { session_token: sessionToken, lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy_m: pos.coords.accuracy });
        if (data?.nearby) { setHit(data.nearby); setDismissed(false); } else setHit(null);
      } catch { /* silenzioso */ }
    }, () => { localStorage.setItem(KEY, "denied"); setOptin("denied"); }, { enableHighAccuracy: false, maximumAge: 120000, timeout: 10000 });
  };
  useEffect(() => {
    if (optin !== "yes") return undefined;
    check();
    timer.current = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(timer.current);
  }, [optin, card]); // eslint-disable-line
  if (optin === "denied" || optin === "no") return null;
  if (!optin) {
    return (
      <div className="card-elev p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="proximity-optin">
        <MapPin className="h-6 w-6 text-amber-300 shrink-0" />
        <div className="flex-1 text-sm text-zinc-200">
          <b>Avvisi quando sei vicino a un punto vendita</b>
          <div className="text-xs text-zinc-400 mt-0.5">Usiamo la tua posizione solo mentre l'app è aperta per avvisarti di offerte e coupon quando passi vicino a una sede. Puoi disattivarlo quando vuoi.</div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => { localStorage.setItem(KEY, "yes"); setOptin("yes"); }} className="pill pill-gold text-xs" data-testid="proximity-optin-yes">Attiva</button>
          <button type="button" onClick={() => { localStorage.setItem(KEY, "no"); setOptin("no"); }} className="pill text-xs border border-zinc-700 text-zinc-300" data-testid="proximity-optin-no">No grazie</button>
        </div>
      </div>
    );
  }
  if (!hit || dismissed) return null;
  return (
    <div className="rounded-2xl p-4 border border-amber-400/40 bg-amber-500/10 relative" data-testid="proximity-banner">
      <button type="button" onClick={() => setDismissed(true)} className="absolute top-2 right-2 text-zinc-400" aria-label="Chiudi" data-testid="proximity-banner-close"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3">
        <MapPin className="h-6 w-6 text-amber-300 shrink-0 mt-0.5" />
        <div className="text-sm text-zinc-100">
          <div className="text-[10px] uppercase tracking-widest text-amber-300">A {hit.distance_m} m · {hit.branch.name}</div>
          <div className="mt-1" data-testid="proximity-banner-text">{hit.text}</div>
          {hit.flash && (
            <div className="mt-2 rounded-xl border border-amber-400/60 bg-amber-400/20 px-3 py-2" data-testid="proximity-banner-flash">
              <div className="text-[10px] uppercase tracking-widest text-amber-200">⚡ Offerta lampo · fino alle {new Date(hit.flash.ends_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
              <div className="font-semibold mt-0.5">{hit.flash.title}</div>
              <div className="text-xs text-zinc-200">{hit.flash.text}</div>
            </div>
          )}
          {hit.coupon && (
            <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-zinc-950/60 border border-zinc-700 px-3 py-1.5 text-xs" data-testid="proximity-banner-coupon">
              <Ticket className="h-3.5 w-3.5 text-amber-300" /> Coupon attivo: <b>{hit.coupon.name}</b> · codice <code className="text-amber-200">{hit.coupon.code}</code>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
